import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  Directive,
  inject,
  input,
  model,
} from '@angular/core';
import { LucideMenu } from '@lucide/angular';
import { MmscanButton } from '@mmscan/angular-ui/button';
import { MMSCAN_UI_I18N } from '@mmscan/angular-ui/core';
import { MMSCAN_DIALOG_PARTS, MmscanDialog } from '@mmscan/angular-ui/dialog';
import { MmscanIcon } from '@mmscan/angular-ui/icon';
import { MmscanMenubar } from './menubar';
import type { MmscanNavItem } from './nav-item';
import { MmscanSidebar } from './sidebar';

export type MmscanNavbarBreakpoint = 'md' | 'lg' | 'xl';

// Full class names so Tailwind finds them.
const WIDE_ONLY: Record<MmscanNavbarBreakpoint, string> = {
  md: 'hidden min-w-0 flex-1 md:block',
  lg: 'hidden min-w-0 flex-1 lg:block',
  xl: 'hidden min-w-0 flex-1 xl:block',
};
const NARROW_ONLY: Record<MmscanNavbarBreakpoint, string> = {
  md: 'md:hidden',
  lg: 'lg:hidden',
  xl: 'xl:hidden',
};

/** Logo / app name at the start of an `mmscan-navbar`. */
@Directive({ selector: '[mmscanNavbarBrand]', host: { class: 'inline-flex shrink-0 items-center gap-2 rounded-sm' } })
export class MmscanNavbarBrand {}

/** Actions at the end of an `mmscan-navbar` (notifications, settings, account). */
@Directive({ selector: '[mmscanNavbarEnd]', host: { class: 'ml-auto flex shrink-0 items-center gap-1' } })
export class MmscanNavbarEnd {}

/**
 * Application header: brand, main navigation and actions. From `collapseBelow` down, the
 * menubar becomes a menu button that opens a drawer with the same `items` as a sidebar; the
 * drawer closes when an item is chosen. Replaces the `p-menubar`-based header; for a full page
 * frame with a sidebar and footer use `mmscan-app-shell`.
 *
 * ```html
 * <mmscan-navbar [items]="items" ariaLabel="Main">
 *   <a mmscanNavbarBrand routerLink="/home"><img src="logo.svg" alt="Mitsubishi Motors" /></a>
 *   <div mmscanNavbarEnd><app-notifications /><app-account-menu /></div>
 * </mmscan-navbar>
 * ```
 */
@Component({
  selector: 'mmscan-navbar',
  imports: [MmscanButton, MmscanIcon, MmscanMenubar, MmscanSidebar, MmscanDialog, ...MMSCAN_DIALOG_PARTS],
  template: `
    <header class="flex h-12 items-center gap-3 border-b border-border bg-background px-4 text-foreground">
      @if (items().length || sectionItems().length) {
        <button
          mmscanButton
          variant="ghost"
          severity="secondary"
          size="icon"
          aria-haspopup="dialog"
          [class]="narrowOnly()"
          [attr.aria-label]="i18n().openMenu"
          [attr.aria-expanded]="menuOpen()"
          (click)="menuOpen.set(true)"
        >
          <mmscan-icon [icon]="menuIcon" size="md" />
        </button>
      }
      <ng-content select="[mmscanNavbarBrand]" />
      @if (items().length && menubar()) {
        <div [class]="wideOnly()">
          <mmscan-menubar [items]="items()" [ariaLabel]="ariaLabel()" [accentIcons]="accentIcons()" />
        </div>
      }
      <ng-content />
      <ng-content select="[mmscanNavbarEnd]" />
    </header>

    <mmscan-drawer [(open)]="menuOpen" position="left" size="sm">
      <ng-template>
        <mmscan-dialog-header>
          <h2 mmscanDialogTitle>{{ ariaLabel() ?? i18n().menu }}</h2>
        </mmscan-dialog-header>
        <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -- delegated: closes the drawer after a link or button inside is activated -->
        <div mmscanDialogContent class="flex flex-col gap-4" (click)="closeOnChoice($event)">
          @if (items().length) {
            <mmscan-sidebar [items]="items()" [ariaLabel]="ariaLabel()" [accentIcons]="accentIcons()" />
          }
          @if (sectionItems().length) {
            <mmscan-sidebar
              [class.border-t]="items().length"
              class="border-border pt-2"
              [items]="sectionItems()"
              [heading]="sectionHeading()"
              [ariaLabel]="i18n().sectionNavigation"
              [accentIcons]="accentIcons()"
            />
          }
        </div>
      </ng-template>
    </mmscan-drawer>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class MmscanNavbar {
  protected readonly i18n = inject(MMSCAN_UI_I18N);
  protected readonly menuIcon = LucideMenu;

  readonly items = input<readonly MmscanNavItem[]>([]);
  /** Name of the navigation landmark (also the drawer title). */
  readonly ariaLabel = input<string>();
  /**
   * Show `items` as a menubar on wide screens. Set to `false` when an app sidebar holds the
   * navigation there, so `items` only feed the small-screen drawer.
   */
  readonly menubar = input(true, { transform: booleanAttribute });
  /** Below this breakpoint the menubar collapses into a menu button and drawer. */
  readonly collapseBelow = input<MmscanNavbarBreakpoint>('lg');
  /** Page-section links (an app sidebar's items), listed under `items` in the small-screen drawer. */
  readonly sectionItems = input<readonly MmscanNavItem[]>([]);
  readonly sectionHeading = input<string>();
  /** Colour item icons with the primary colour. */
  readonly accentIcons = input(false, { transform: booleanAttribute });
  /** Whether the small-screen menu drawer is open. */
  readonly menuOpen = model(false);

  protected readonly wideOnly = computed(() => WIDE_ONLY[this.collapseBelow()]);
  protected readonly narrowOnly = computed(() => NARROW_ONLY[this.collapseBelow()]);

  /** Choosing a link or command closes the drawer; group toggles keep it open. */
  protected closeOnChoice(event: Event): void {
    const target = (event.target as HTMLElement).closest('a, button');
    if (target && !target.hasAttribute('aria-controls')) this.menuOpen.set(false);
  }
}

/** Navbar and its slot directives. */
export const MMSCAN_NAVBAR = [MmscanNavbar, MmscanNavbarBrand, MmscanNavbarEnd] as const;
